import axios from 'axios'
import React, {useState} from 'react'
import {Modal, Button} from 'react-bootstrap'
import {useDispatch} from 'react-redux'
import {configuration} from 'src/configuration'
import {setMessage} from 'src/store/alertMessage/actions'
import {formatDate} from 'src/utils'
import {constants} from 'src/utils/const'

const API_URL = configuration.apiUrl

interface Props {
  show: boolean
  onHide: () => void
  isBackupRunning: boolean
  setIsBackupRunning: React.Dispatch<React.SetStateAction<boolean>>
}

const DatabaseBackupModal: React.FC<Props> = ({
  show,
  onHide,
  isBackupRunning,
  setIsBackupRunning,
}) => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  const handleSubmit = async () => {
    setLoading(true)
    let canStartBackup = false

    try {
      // 1. Check backup status first
      const statusResponse = await axios.get(`${API_URL}/databasebackup/getBackupStatus`)
      const isRunning = statusResponse.data?.data?.is_backup_running

      if (isRunning) {
        onHide()
        dispatch(
          setMessage(
            'info',
            'A backup process is already running. Please wait until it completes.',
            4
          )
        )
        setLoading(false)
        return
      } else {
        canStartBackup = true
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        canStartBackup = true
      } else {
        onHide()
        dispatch(
          setMessage(
            'error',
            error?.response?.data?.responseMessage ??
              error?.message ??
              'Could not check backup status'
          )
        )
        setLoading(false)
        return
      }
    }

    if (canStartBackup) {
      onHide()
      dispatch(
        setMessage(
          'info',
          'Your database backup process has started. You will be notified once it is complete.',
          2
        )
      )
      axios
        .post(`${API_URL}/databasebackup/runBackup`)
        .then((response) => {
          const mysqlResult = response.data?.results?.mysql
          if (mysqlResult?.status === 'success') {
            dispatch(
              setMessage('success', mysqlResult.message ?? response.data?.responseMessage, 3)
            )
          } else {
            // Covers mysql.status === 'error' or missing
            dispatch(setMessage('error', 'Backup failed. Please try again later.', 4))
          }
          setIsBackupRunning(false)
        })
        .catch((error: any) => {
          if (error?.response?.status === 409) {
            dispatch(
              setMessage(
                'info',
                error?.response?.data?.responseMessage ??
                  'A backup process is already running. Please wait until it completes. Try again after it completes.',
                4
              )
            )
          } else {
            dispatch(setMessage('error', 'Backup failed. Please try again later.', 4))
          }
          setIsBackupRunning(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
    setLoading(false)
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Database Backup Confirmation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Are you sure you want to start a database backup at{' '}
          <b>{formatDate(new Date().toISOString(), constants.DATE_FORMAT.DD_MM_YYYY_HH_MM_A)}</b>?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='primary' onClick={handleSubmit} disabled={loading}>
          {loading ? 'Processing...' : 'Back up'}
        </Button>
        <Button variant='secondary' onClick={onHide} disabled={loading}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DatabaseBackupModal
